import { QueryKey, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { useGetDisplayColumn } from '@/api/display-column'
import {
  DisplayColumnDashboard,
  DisplayColumnFields,
  DisplayColumnKey,
  DisplayFieldType,
} from '@/models/features/display-column.models'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { selectUserRole } from '@/reducers/userDataReducer'

import {
  DEFAULT_FIELDS_BY_TYPE,
  HIGHLIGHT_DURATION_MS,
  ROLES_ALLOWED_TO_EDIT,
} from './person-column.constants'
import { PersonColumnSettings } from './person-column.types'

export const pickDisplayColumnFields = (fields: DisplayColumnFields): DisplayColumnFields => ({
  apartment: !!fields.apartment,
  avatar: !!fields.avatar,
  buildingNumber: !!fields.buildingNumber,
  email: !!fields.email,
  firstName: true,
  lastName: true,
  phone: !!fields.phone,
  role: !!fields.role,
  street: !!fields.street,
})

interface UsePersonColumnSettingsParams {
  dashboard: DisplayColumnDashboard
  column: DisplayColumnKey
  fieldType: DisplayFieldType
  // Note: the query key (or its prefix) of the table's rows - set once the page renders from the
  // BE's `<column>Display`, so Save refetches them (see PersonColumnSettings.refreshRows).
  rowsQueryKey?: QueryKey
}

// Note: the saved "Column data settings" of one person column for the active building. Until they
// load (or if they fail to) the column renders its defaults - the table never waits on them.
export const usePersonColumnSettings = ({
  dashboard,
  column,
  fieldType,
  rowsQueryKey,
}: UsePersonColumnSettingsParams): PersonColumnSettings => {
  const queryClient = useQueryClient()
  const activeBuilding = useSelector(selectActiveBuilding)
  const userRole = useSelector(selectUserRole)
  const { data } = useGetDisplayColumn({ buildingId: activeBuilding?.id, column, dashboard })
  const [highlighted, setHighlighted] = useState(false)

  useEffect(() => {
    if (!highlighted) return
    const timeout = setTimeout(() => setHighlighted(false), HIGHLIGHT_DURATION_MS)
    return () => clearTimeout(timeout)
  }, [highlighted])

  return {
    canEdit: ROLES_ALLOWED_TO_EDIT.includes(userRole),
    column,
    dashboard,
    fields: data ? pickDisplayColumnFields(data) : DEFAULT_FIELDS_BY_TYPE[fieldType],
    fieldType,
    highlighted,
    onSaved: () => setHighlighted(true),
    refreshRows: rowsQueryKey
      ? () => queryClient.invalidateQueries({ queryKey: rowsQueryKey })
      : undefined,
  }
}
